import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Flag, ArrowLeft, Paperclip, CheckCircle } from "lucide-react";
import { fetchPublicProfileByUserId, createModerationReport } from "../../api/profileService";
import { useAuth } from "../../context/AuthContext";

const CATEGORIES = [
  { value: "comportement", label: "Comportement inadapté" },
  { value: "triche", label: "Triche" },
  { value: "faux_compte", label: "Faux compte" },
  { value: "autre", label: "Autre" },
];

export default function ReportProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [targetProfile, setTargetProfile] = useState(null);
  const [category, setCategory] = useState("comportement");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) return;
    fetchPublicProfileByUserId(userId)
      .then((data) => setTargetProfile(data))
      .catch(() => {});
  }, [userId]);

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId || isSubmitting) return;
    setError("");
    setIsSubmitting(true);
    try {
      await createModerationReport(
        {
          target_user_id: userId,
          context_type: "profile",
          category,
          reason: category,
          description,
        },
        file || null
      );
      setSuccess(true);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Connexion requise pour signaler."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="mx-auto max-w-lg px-4 py-16">
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-8 text-center text-rose-700">
            Vous devez être connecté pour signaler un joueur.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(95,181,111,0.12),transparent_30%),linear-gradient(180deg,#fffdf5_0%,#f5f7ff_100%)]">
      <div className="mx-auto max-w-lg px-4 py-10 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>

        <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-100">
              <Flag className="h-5 w-5 text-rose-600" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Signaler un joueur</h1>
              {targetProfile?.username && (
                <p className="text-sm text-slate-500">
                  Vous signalez{" "}
                  <span className="font-semibold text-slate-700">
                    {targetProfile.username}
                  </span>
                </p>
              )}
            </div>
          </div>

          {success ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <CheckCircle className="h-12 w-12 text-emerald-500" />
              <div>
                <div className="text-lg font-bold text-slate-900">Signalement envoyé</div>
                <p className="mt-1 text-sm text-slate-500">
                  Notre équipe de modération examinera votre signalement.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mt-2 rounded-2xl bg-slate-800 px-6 py-2.5 text-sm font-bold text-white hover:bg-slate-700"
              >
                Retour
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Motif
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`rounded-2xl border px-3 py-2.5 text-sm font-semibold transition ${
                        category === cat.value
                          ? "border-rose-400 bg-rose-50 text-rose-700"
                          : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Description (optionnelle)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez le problème..."
                  rows={4}
                  maxLength={2000}
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none placeholder:text-slate-400 focus:border-sky-400"
                />
                <div className="mt-1 text-right text-xs text-slate-400">
                  {description.length}/2000
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Pièce jointe (optionnelle)
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 hover:border-slate-400 hover:bg-slate-100">
                  <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="truncate">
                    {file ? file.name : "Ajouter une capture d'écran ou un fichier"}
                  </span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>
                {file && (
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="mt-1.5 text-xs text-slate-400 hover:text-rose-600"
                  >
                    Supprimer le fichier
                  </button>
                )}
              </div>

              {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-2xl bg-rose-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-rose-700 disabled:opacity-60"
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer le signalement"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
